import type { StepNode } from '@/api/types'

/**
 * Phase 13: pure functions for the Step DAG. The doing-view computes a readiness map every render
 * from `(snapshotSteps, completedStepIds)` — no server data — and uses it to mute blocked rows,
 * label them with their unmet prerequisites, and surface a "Next Up" pointer to the smallest
 * leaf Action that's actually doable. Same algorithm runs in the editor's `<DependencyPicker>` to
 * compute exclusions (self / descendants / would-create-cycle) and offer matching defense in
 * depth on top of the server-side validators.
 */

export interface StepReadiness {
  blocked: boolean
  /**
   * Each unmet prerequisite as `{ id, title }`. UUIDs that don't resolve to a Step in the tree
   * (the dep target was removed) surface as `title: '(removed step)'` so the editor can offer a
   * one-click "clear orphans" affordance — silent filtering would hide that signal.
   */
  blockedBy: { id: string; title: string }[]
}

/**
 * Walk every Step in the tree (including nested project-reference snapshots) and compute its
 * readiness against `completedStepIds`. Sections derive completion from their descendants; a
 * blocked Action is one whose `dependsOn` contains an id that isn't (transitively) complete.
 */
export const computeStepReadiness = (
  steps: StepNode[],
  completedStepIds: Set<string>
): Map<string, StepReadiness> => {
  const byId = new Map<string, StepNode>()
  collectById(steps, byId)

  // Memoize per-step completeness so a repeated lookup over a wide Section subtree stays linear.
  const completeCache = new Map<string, boolean>()
  const isComplete = (stepId: string, ancestors: Set<string>): boolean => {
    const cached = completeCache.get(stepId)
    if (cached !== undefined) return cached
    if (ancestors.has(stepId)) {
      // Cycle in stored data (shouldn't happen — backend prevents it). Treat the cycle members as
      // incomplete so blocked-by computation terminates with a sensible answer.
      completeCache.set(stepId, false)
      return false
    }
    const step = byId.get(stepId)
    if (!step) {
      completeCache.set(stepId, false)
      return false
    }
    const result = computeIsComplete(step, completedStepIds, ancestors, isComplete)
    completeCache.set(stepId, result)
    return result
  }

  const readiness = new Map<string, StepReadiness>()
  for (const step of byId.values()) {
    const blockedBy: { id: string; title: string }[] = []
    for (const depId of step.dependsOn) {
      if (!isComplete(depId, new Set([step.id]))) {
        const target = byId.get(depId)
        blockedBy.push({ id: depId, title: target?.title ?? '(removed step)' })
      }
    }
    readiness.set(step.id, { blocked: blockedBy.length > 0, blockedBy })
  }
  return readiness
}

const computeIsComplete = (
  step: StepNode,
  completedStepIds: Set<string>,
  ancestors: Set<string>,
  isComplete: (id: string, ancestors: Set<string>) => boolean
): boolean => {
  if (step.kind === 'ACTION') {
    return completedStepIds.has(step.id)
  }
  // SECTION / PROJECT_REFERENCE: complete iff every leaf descendant Action is complete. Empty
  // Sections are vacuously "complete" — they don't block anything that depends on them.
  return everyDescendantActionComplete(step, completedStepIds, ancestors, isComplete)
}

const everyDescendantActionComplete = (
  step: StepNode,
  _completedStepIds: Set<string>,
  ancestors: Set<string>,
  isComplete: (id: string, ancestors: Set<string>) => boolean
): boolean => {
  if (step.children.length === 0) return true
  const nextAncestors = new Set(ancestors).add(step.id)
  for (const child of step.children) {
    if (!isComplete(child.id, nextAncestors)) return false
  }
  return true
}

const collectById = (steps: StepNode[], out: Map<string, StepNode>): void => {
  for (const step of steps) {
    out.set(step.id, step)
    collectById(step.children, out)
  }
}

/**
 * Tree-order walk to find the first incomplete leaf Action whose dependencies are all satisfied.
 * "Tree order" here is depth-first preorder over the same shape the outline renders. Returns null
 * when every leaf Action is either complete or blocked.
 */
export const findFirstReadyAction = (
  steps: StepNode[],
  completedStepIds: Set<string>,
  readiness: Map<string, StepReadiness>
): StepNode | null => {
  for (const step of steps) {
    if (step.kind === 'ACTION' && step.children.length === 0) {
      if (completedStepIds.has(step.id)) continue
      if (readiness.get(step.id)?.blocked) continue
      return step
    }
    const fromChild = findFirstReadyAction(step.children, completedStepIds, readiness)
    if (fromChild) return fromChild
  }
  return null
}

/**
 * Compares `prevSteps` against `nextSteps` step-by-step and surfaces dependencies that will be
 * dropped on refresh — i.e., a UUID present in some Step's `dependsOn` in `prev` but no longer in
 * `next`. The doing-view's refresh-diff dialog renders the result as "X dependencies will be
 * cleared" alongside structural changes + acquisition orphans.
 */
export const findOrphanedDependencies = (
  prevSteps: StepNode[],
  nextSteps: StepNode[]
): { stepId: string; stepTitle: string; droppedDeps: { id: string; title: string }[] }[] => {
  const prevById = new Map<string, StepNode>()
  collectById(prevSteps, prevById)
  const nextById = new Map<string, StepNode>()
  collectById(nextSteps, nextById)

  const out: { stepId: string; stepTitle: string; droppedDeps: { id: string; title: string }[] }[] =
    []
  for (const [id, prev] of prevById) {
    const next = nextById.get(id)
    if (!next) continue
    const nextDeps = new Set(next.dependsOn)
    const dropped = prev.dependsOn.filter((d) => !nextDeps.has(d))
    if (dropped.length > 0) {
      out.push({
        stepId: id,
        stepTitle: prev.title,
        droppedDeps: dropped.map((d) => ({
          id: d,
          title: prevById.get(d)?.title ?? '(removed step)',
        })),
      })
    }
  }
  return out
}

/**
 * Frontend mirror of the backend cycle check. Returns true if adding `candidateDepId` to
 * `editedStepId`'s `dependsOn` would create a cycle. Used by `<DependencyPicker>` to gray out
 * entries that the server would reject anyway.
 */
export const wouldCreateCycle = (
  steps: StepNode[],
  editedStepId: string,
  candidateDepId: string
): boolean => {
  if (editedStepId === candidateDepId) return true
  const byId = new Map<string, StepNode>()
  collectById(steps, byId)
  // Forward DFS from candidateDepId along existing dependsOn edges; if we reach editedStepId, the
  // proposed addition would close a cycle.
  const stack: string[] = [candidateDepId]
  const visited = new Set<string>()
  while (stack.length > 0) {
    const current = stack.pop()!
    if (current === editedStepId) return true
    if (!visited.add(current)) continue
    const step = byId.get(current)
    if (!step) continue
    for (const dep of step.dependsOn) stack.push(dep)
  }
  return false
}

/**
 * Returns the set of Step ids that should be excluded from the picker for `editedStepId`: self,
 * every descendant of self, plus every Step that (transitively) depends on self.
 */
export const computePickerExclusions = (steps: StepNode[], editedStepId: string): Set<string> => {
  const excluded = new Set<string>([editedStepId])
  const byId = new Map<string, StepNode>()
  collectById(steps, byId)
  const editedStep = byId.get(editedStepId)
  if (editedStep) collectStepIds(editedStep.children, excluded)
  for (const step of byId.values()) {
    if (!excluded.has(step.id) && wouldCreateCycle(steps, editedStepId, step.id)) {
      excluded.add(step.id)
    }
  }
  return excluded
}

const collectStepIds = (steps: StepNode[], out: Set<string>): void => {
  for (const step of steps) {
    out.add(step.id)
    collectStepIds(step.children, out)
  }
}
